import { useEffect, useRef, useState } from 'react';
import {
  useSaveServiceOrderMutation,
  useDeleteServiceOrderMutation,
  useGetOrderCompanyContactsQuery,
} from 'app/services/projectBoardApi';
import type { IBoardUser, ICompany, IServiceOrder } from 'models';
import {
  PHONE_PATTERN,
  useOrderCreatorName,
  scrollMainContainerToCard,
  ClearableInput,
  OrderTextarea,
  OrderDateInput,
  OrderTimeInput,
  DeleteOrderPopup,
} from '../orders/orderShared';
import ServiceNumberInput from './ServiceNumberInput';

/**
 * ServiceOrderView (`li.col-xs-12`, service_order.tmpl) — inventory §B7 /
 * parity 193–198. Shipping-clone card with the service field set; faithful
 * markup port incl. the template quirks: the heading checkbox id is STILL
 * `shipping_order_no{{id}}`; the summary reuses the shipping collapsed ids
 * (`collapsed_sched_ship_date`, `collapsed_equipment_for_delivery` twice,
 * `collapsed_freight_co_id/_contact_id`); the address country div has no
 * `not-empty` conditional; the Work Start SCHEDULED column wrapper is
 * `actual-inputs` and its date input's `.empty` keys off the (always-absent)
 * `sched_loading_date` ⇒ permanently `.empty`; Title/Order No. maxlength 35,
 * equipment 129, technician name 55, address lines 30 — the TEMPLATE values
 * (the inventory's B7 prose lists a few differently; the live tmpl wins).
 *
 * SAVE CONTRACT (parity 196 — PINNED, see saveServiceOrder): every field
 * commit sends the FULL model as PUT /api/v1.0/projects/services/{id}; the
 * body includes project_id (server-required) and any locally-held `false`s
 * from the number quirk; field-validation failures 400 the WHOLE save.
 *
 * NUMBER FIELDS (parity 195): ServiceNumberInput replicates
 * round/2dp/comma/`.invalid` and the invalid-saves-FALSE quirk EXACTLY.
 *
 * CONTACT OPTIONS QUIRK (B7): the template gates the contact <option>s on
 * `service_co_contact_id != null` (not the company id — unlike Shipping), so
 * with a company set but no contact chosen the select shows only `-----`
 * after any re-render; selecting a company DOM-patches the options in
 * (updateCompanyContactField) until the next model-change re-render wipes
 * them. Replicated via `companyJustChanged` (set on a company commit,
 * cleared by any other field commit).
 *
 * DELETE (parity 198): ONE confirm binding. The legacy view binds BOTH its
 * own `confirm_delete` click AND the popup's remover_function — confirm
 * fires destroy() twice and the second DELETE 404s. [NEG] — not ported.
 */
const ServiceOrderCard = ({
  projectId,
  order,
  companies,
  me,
  access,
  initiallyExpanded,
  autoFocusTitle,
}: {
  projectId: number;
  order: IServiceOrder;
  companies: ICompany[];
  me: IBoardUser;
  access: boolean;
  initiallyExpanded: boolean;
  autoFocusTitle: boolean;
}) => {
  const [saveOrder] = useSaveServiceOrderMutation();
  const [deleteOrder] = useDeleteServiceOrderMutation();
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [showDelete, setShowDelete] = useState(false);
  const [companyJustChanged, setCompanyJustChanged] = useState(false);
  const liRef = useRef<HTMLLIElement | null>(null);
  const newCardBootRef = useRef(false);

  // New-card boot (expanded + order_title focus) — see ShippingOrderCard.
  useEffect(() => {
    if (autoFocusTitle && !newCardBootRef.current) {
      newCardBootRef.current = true;
      setExpanded(true);
      liRef.current
        ?.querySelector<HTMLInputElement>('input[name="order_title"]')
        ?.focus();
    }
  }, [autoFocusTitle]);

  const locked = !access;
  const creatorName = useOrderCreatorName(order.order_created_by, me);

  const coId = order.service_co_id;
  const { data: contacts } = useGetOrderCompanyContactsQuery(Number(coId), {
    skip: coId == null,
  });

  const companyName = companies.find((c) => c.id === Number(coId))?.name ?? '';
  const contactName =
    order.service_co_contact_id != null
      ? contacts?.find((c) => c.id === Number(order.service_co_contact_id))?.name ?? ''
      : '';

  // FULL-MODEL save: body = the whole cached model + the local set (which
  // also lands in the cache up-front and is KEPT on failure).
  const save = (set: Record<string, unknown>) => {
    saveOrder({ projectId, id: order.id, set, body: { ...order, ...set } });
  };

  const commit = (name: string, value: unknown) => {
    setCompanyJustChanged(false); // any model change re-render wipes injected options
    save({ [name]: value });
  };

  const commitCompany = (raw: string) => {
    let v: number | null = parseInt(raw, 10);
    if (!v) v = null;
    setCompanyJustChanged(true); // updateCompanyContactField DOM patch
    save({ service_co_id: v, service_co_contact_id: null });
  };

  const commitContact = (raw: string) => {
    let v: number | null = parseInt(raw, 10);
    if (!v) v = null;
    setCompanyJustChanged(false);
    save({ service_co_contact_id: v });
  };

  const toggleCollapse = () => {
    setExpanded((x) => !x);
    scrollMainContainerToCard(liRef.current);
  };

  const v = (key: keyof IServiceOrder): string =>
    (order[key] as string | null | undefined) ?? '';

  const contactOptionsVisible =
    coId != null && (order.service_co_contact_id != null || companyJustChanged);

  return (
    <li className="col-xs-12" ref={liRef}>
      <div className="panel-group col-xs-12" id={`accordion${order.id}`}>
        <div className="panel panel-default row">
          <div
            className={`panel-heading col-xs-12${expanded ? '' : ' collapsed'}`}
            data-toggle="collapse"
            data-parent={`#accordion${order.id}`}
            onClick={(e) => {
              // Same double-fire guard as ShippingOrderCard (3e verify finding 2):
              // label/checkbox clicks toggle via onChange; heading takes the rest.
              const t = e.target as HTMLElement;
              if (t.closest('label.collapse-btn') || t.closest('input.collapse-checkbox')) return;
              toggleCollapse();
            }}
          >
            {/* Template quirk: the service checkbox id is still shipping_order_no. */}
            <input
              id={`shipping_order_no${order.id}`}
              type="checkbox"
              className="hidden collapse-checkbox"
              tabIndex={-1}
              checked={expanded}
              onChange={toggleCollapse}
            />
            <label
              htmlFor={`shipping_order_no${order.id}`}
              className="icon-add right collapse-btn ka-has-tooltip pointer"
              tooltip="Expand"
              tabIndex={-1}
            />
            <div className="collapsed-mode list-inline">
              <div className="col-xs-12 distance">
                <div className="col-xs-2">
                  <div className="row">
                    <div className="value" id="collapsed_order_title">
                      {v('order_title')}
                    </div>
                  </div>
                </div>
                <div className="col-xs-10">
                  <div className="row">
                    <div className="col-xs-4">
                      <div className="row ">
                        <div className="label-text col-xs-5">Order No.</div>
                        <div className="value col-xs-7" id="collapsed_order_number">
                          {v('order_number')}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4 ">
                      <div className="row">
                        <div className="label-text col-xs-5">Scheduled Start</div>
                        <div className="value col-xs-7" id="collapsed_sched_ship_date">
                          {v('sched_service_start_date')}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4 ">
                      <div className="row">
                        <div className="label-text col-xs-5">Actual Start</div>
                        <div className="value col-xs-7" id="collapsed_equipment_for_delivery">
                          {v('actual_service_start_date')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xs-12 distance">
                <div className="col-xs-10 col-xs-offset-2">
                  <div className="row">
                    <div className="col-xs-4">
                      <div className="row">
                        <div className="label-text col-xs-5">Equipment</div>
                        <div className="value col-xs-7" id="collapsed_equipment_for_delivery">
                          {v('equipment_for_service')}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4">
                      <div className="row">
                        <div className="label-text col-xs-5">Scheduled Completion</div>
                        <div className="value col-xs-7" id="collapsed_sched_delivery_date">
                          {v('sched_service_complete_date')}
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-4">
                      <div className="row">
                        <div className="label-text col-xs-5">Actual Completion</div>
                        <div className="value col-xs-7" id="collapsed_actual_delivery_date">
                          {v('actual_service_complete_date')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xs-12 distance">
                <div className="col-xs-10 col-xs-offset-2">
                  <div className="row">
                    <div className="col-xs-4">
                      <div className="row">
                        <div className="label-text col-xs-5">Service Co.</div>
                        <div className="value col-xs-7">
                          <div className="row">
                            <div className="col-xs-12" id="collapsed_freight_co_id">
                              {companyName}
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-xs-12" id="collapsed_freight_co_contact_id">
                              {contactName}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-8">
                      <div className="row">
                        <div className="label-text col-xs-3 custom-col-xs-3 ">Address</div>
                        <div className="value col-xs-9">
                          <div
                            className={`left${order.service_address_line_1 ? ' not-empty' : ''}`}
                            id="collapsed_service_address_line_1"
                          >
                            {v('service_address_line_1')}
                          </div>
                          <div
                            className={`left${order.service_address_line_2 ? ' not-empty ' : ''}`}
                            id="collapsed_service_address_line_2"
                          >
                            {v('service_address_line_2')}
                          </div>
                          <div
                            className={`left${order.service_address_city ? ' not-empty ' : ''}`}
                            id="collapsed_service_address_city"
                          >
                            {v('service_address_city')}
                          </div>
                          <div
                            className={`left${order.service_address_st ? ' not-empty ' : ''}`}
                            id="collapsed_service_address_st"
                          >
                            {v('service_address_st')}
                          </div>
                          <div
                            className={`left${order.service_address_postal_code ? ' not-empty ' : ''}`}
                            id="collapsed_service_address_postal_code"
                          >
                            {v('service_address_postal_code')}
                          </div>
                          {/* Template quirk: country has no not-empty conditional. */}
                          <div className="left" id="collapsed_service_address_country">
                            {v('service_address_country')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            id={`collapse${order.id}`}
            className={`panel-collapse collapse${expanded ? ' in' : ''} col-xs-12`}
          >
            <div className="row">
              <div className="col-xs-12">
                <div className="row">
                  <div className="col-lg-6 col-md-12">
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="label-text col-xs-2">Title</div>
                        <div className="col-xs-10">
                          <ClearableInput
                            className="col-xs-12"
                            name="order_title"
                            value={v('order_title')}
                            maxLength={35}
                            locked={locked}
                            onCommit={commit}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="label-text col-xs-2">Order No.</div>
                        <div className="col-xs-10">
                          <ClearableInput
                            className="col-xs-12"
                            name="order_number"
                            value={v('order_number')}
                            maxLength={35}
                            locked={locked}
                            onCommit={commit}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="label-text col-xs-2">Description</div>
                        <div className="col-xs-10">
                          <ClearableInput
                            className="col-xs-12"
                            name="order_description"
                            value={v('order_description')}
                            maxLength={55}
                            locked={locked}
                            onCommit={commit}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="label-text col-xs-2">By</div>
                        <div className="col-xs-10">
                          <input
                            className="col-xs-12"
                            type="text"
                            name="order_created_by"
                            value={creatorName}
                            readOnly
                            minLength={0}
                            maxLength={35}
                            tabIndex={-1}
                            onChange={() => undefined}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="label-text col-xs-2">Date created</div>
                        <div className="col-xs-10">
                          <input
                            className="col-xs-12"
                            type="text"
                            name="order_created_date"
                            value={v('order_created_date')}
                            readOnly
                            minLength={0}
                            maxLength={15}
                            tabIndex={-1}
                            onChange={() => undefined}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6 col-md-12">
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="date-table-heading">
                        <div className="col-xs-6">
                          <div className="row">SCHEDULED</div>
                        </div>
                        <div className="col-xs-6">ACTUAL</div>
                      </div>
                    </div>
                    {/* ---- Work Start ---- */}
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="col-xs-3 label-text">Work Start</div>
                        <div className="col-xs-9">
                          <div className="row">
                            {/* Template quirk: the SCHEDULED wrapper is
                                `actual-inputs`, and the date's .empty keys off
                                the never-present sched_loading_date. */}
                            <div className="actual-inputs col-xs-6">
                              <OrderDateInput
                                name="sched_service_start_date"
                                value={v('sched_service_start_date')}
                                emptyFrom={null}
                                locked={locked}
                                tabIndex={-1}
                                autoComplete="off"
                                onCommit={commit}
                              />
                              <OrderTimeInput
                                className="time-input pointer"
                                name="sched_service_start_time"
                                value={v('sched_service_start_time')}
                                locked={locked}
                                onCommit={commit}
                              />
                            </div>
                            <div className="actual-inputs col-xs-6">
                              <OrderDateInput
                                name="actual_service_start_date"
                                value={v('actual_service_start_date')}
                                locked={locked}
                                tabIndex={-1}
                                onCommit={commit}
                              />
                              <OrderTimeInput
                                className="time-input  pointer"
                                name="actual_service_start_time"
                                value={v('actual_service_start_time')}
                                locked={locked}
                                onCommit={commit}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* ---- Work Complete ---- */}
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="col-xs-3 label-text">Work Complete</div>
                        <div className="col-xs-9">
                          <div className="row">
                            <div className="col-xs-6">
                              <OrderDateInput
                                name="sched_service_complete_date"
                                value={v('sched_service_complete_date')}
                                locked={locked}
                                tabIndex={-1}
                                autoComplete="off"
                                onCommit={commit}
                              />
                              <OrderTimeInput
                                className="time-input  pointer"
                                name="sched_service_complete_time"
                                value={v('sched_service_complete_time')}
                                locked={locked}
                                onCommit={commit}
                              />
                            </div>
                            <div className="actual-inputs col-xs-6">
                              <OrderDateInput
                                name="actual_service_complete_date"
                                value={v('actual_service_complete_date')}
                                locked={locked}
                                tabIndex={-1}
                                autoComplete="off"
                                onCommit={commit}
                              />
                              <OrderTimeInput
                                className="time-input  pointer"
                                name="actual_service_complete_time"
                                value={v('actual_service_complete_time')}
                                locked={locked}
                                onCommit={commit}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* ---- Total Time | Trip ---- */}
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="col-xs-3 label-text">Total Time | Trip</div>
                        <div className="col-xs-9">
                          <div className="row">
                            <div className=" col-xs-6 price-inputs">
                              <span className="unit left">hr</span>
                              <ServiceNumberInput
                                name="sched_total_time"
                                value={order.sched_total_time}
                                min={0}
                                step={0.01}
                                max={1000.5}
                                locked={locked}
                                onCommit={commit}
                              />
                              <ServiceNumberInput
                                name="sched_total_trips"
                                value={order.sched_total_trips}
                                min={0}
                                step={1}
                                max={100}
                                locked={locked}
                                onCommit={commit}
                              />
                            </div>
                            <div className=" col-xs-6 price-inputs">
                              <ServiceNumberInput
                                name="actual_total_time"
                                value={order.actual_total_time}
                                min={0}
                                step={0.01}
                                max={1000.5}
                                locked={locked}
                                onCommit={commit}
                              />
                              <ServiceNumberInput
                                name="actual_total_trips"
                                value={order.actual_total_trips}
                                min={0}
                                step={1}
                                max={100}
                                locked={locked}
                                onCommit={commit}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* ---- Cost ---- */}
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="col-xs-3 label-text">Cost</div>
                        <div className="col-xs-9">
                          <div className="row">
                            <div className="price-inputs col-xs-6">
                              <span className="unit left">US$</span>
                              <ServiceNumberInput
                                name="sched_cost"
                                value={order.sched_cost}
                                min={0}
                                step={1}
                                max={100000}
                                locked={locked}
                                onCommit={commit}
                              />
                            </div>
                            <div className="price-inputs col-xs-6">
                              <span className="unit left">US$</span>
                              <ServiceNumberInput
                                name="actual_cost"
                                value={order.actual_cost}
                                min={0}
                                step={1}
                                max={100000}
                                locked={locked}
                                onCommit={commit}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row" style={{ paddingTop: 49, paddingBottom: 20 }}>
                  <div className="col-lg-6 col-md-12" id="equipment">
                    <div className="col-xs-12 input-vertical-distance textarea-top-padding">
                      <div className="row">
                        <div className="col-xs-2 label-text">Equipment</div>
                        <div className="col-xs-10">
                          <OrderTextarea
                            name="equipment_for_service"
                            value={v('equipment_for_service')}
                            maxLength={129}
                            locked={locked}
                            onCommit={commit}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="col-xs-2 label-text">Service Co.</div>
                        <div className="col-xs-10 label-company">
                          <div className="row">
                            <div className="col-xs-6">
                              <select
                                className="service_co_field"
                                name="service_co_id"
                                disabled={locked}
                                value={coId != null ? String(coId) : 'None'}
                                onChange={(e) => commitCompany(e.target.value)}
                              >
                                <option value="None">-----</option>
                                {companies.map((c) => (
                                  <option key={c.id} value={String(c.id)}>
                                    {c.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-xs-6">
                              <select
                                className="xs-input service_co_contact_field"
                                name="service_co_contact_id"
                                disabled={locked}
                                value={
                                  order.service_co_contact_id != null
                                    ? String(order.service_co_contact_id)
                                    : 'None'
                                }
                                onChange={(e) => commitContact(e.target.value)}
                              >
                                <option value="None">-----</option>
                                {contactOptionsVisible &&
                                  (contacts ?? []).map((c) => (
                                    <option key={c.id} value={String(c.id)}>
                                      {c.name}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance">
                      <div className="row">
                        <div className="label-text col-xs-2">Technician</div>
                        <div className="col-xs-10  input-vertical-distance input-with-placeholder-group">
                          <div className="row">
                            <div className="col-xs-6">
                              <ClearableInput
                                className="full-width input-with-placeholder"
                                id="service_tech"
                                name="service_tech"
                                value={v('service_tech')}
                                placeholder="Name"
                                maxLength={55}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label className="input-placeholder" htmlFor="service_tech">
                                Name
                              </label>
                            </div>
                            <div className="col-xs-6">
                              <ClearableInput
                                className="full-width input-with-placeholder phone-number-input"
                                id="service_tech_mob"
                                name="service_tech_mob"
                                value={v('service_tech_mob')}
                                placeholder="Mobile"
                                maxLength={15}
                                pattern={PHONE_PATTERN}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label className="input-placeholder" htmlFor="service_tech_mob">
                                Mobile
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="col-xs-push-2 col-xs-10 input-with-placeholder-group">
                          <div className="row">
                            <div className="col-xs-6">
                              <ClearableInput
                                className="full-width input-with-placeholder phone-number-input"
                                id="service_tech_tel_office"
                                name="service_tech_tel_office"
                                value={v('service_tech_tel_office')}
                                placeholder="Office tel."
                                maxLength={15}
                                pattern={PHONE_PATTERN}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label
                                className="input-placeholder"
                                htmlFor="service_tech_tel_office"
                              >
                                Office tel.
                              </label>
                            </div>
                            <div className="col-xs-6">
                              <ClearableInput
                                className="full-width input-with-placeholder press_english"
                                type="email"
                                id="service_tech_email"
                                name="service_tech_email"
                                value={v('service_tech_email')}
                                placeholder="Email"
                                maxLength={35}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label className="input-placeholder" htmlFor="service_tech_email">
                                Email
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 input-vertical-distance textarea-top-padding">
                      <div className="row">
                        <div className="label-text col-xs-2">Instructions</div>
                        <div className="col-xs-10">
                          <OrderTextarea
                            name="instructions"
                            value={v('instructions')}
                            maxLength={255}
                            locked={locked}
                            onCommit={commit}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6 col-md-12" id="#serviceAddress">
                    <div className="col-xs-12">
                      <div className="row">
                        <div className="label-text col-xs-3">Service Address</div>
                        <div className="col-xs-9 input-with-placeholder-group input-vertical-distance">
                          <ClearableInput
                            className="col-xs-12 input-with-placeholder"
                            id="service_address_line_1"
                            name="service_address_line_1"
                            value={v('service_address_line_1')}
                            placeholder="Address Line 1"
                            maxLength={30}
                            locked={locked}
                            onCommit={commit}
                          />
                          <label className="input-placeholder" htmlFor="service_address_line_1">
                            Address Line 1
                          </label>
                        </div>
                        <div className="col-xs-offset-3 col-xs-9 input-with-placeholder-group input-vertical-distance">
                          <ClearableInput
                            className="col-xs-12 input-with-placeholder"
                            id="service_address_line_2"
                            name="service_address_line_2"
                            value={v('service_address_line_2')}
                            placeholder="Address Line 2"
                            maxLength={30}
                            locked={locked}
                            onCommit={commit}
                          />
                          <label className="input-placeholder" htmlFor="service_address_line_2">
                            Address Line 2
                          </label>
                        </div>
                        <div className="col-xs-offset-3 col-xs-9 input-vertical-distance">
                          <div className="row">
                            <div className="col-xs-4 input-with-placeholder-group">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder"
                                id="service_address_city"
                                name="service_address_city"
                                value={v('service_address_city')}
                                placeholder="City"
                                maxLength={30}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label className="input-placeholder" htmlFor="service_address_city">
                                City
                              </label>
                            </div>
                            <div className="col-xs-2 input-with-placeholder-group">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder no-padding-md"
                                id="service_address_st"
                                name="service_address_st"
                                value={v('service_address_st')}
                                placeholder="State"
                                maxLength={3}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label className="input-placeholder" htmlFor="service_address_st">
                                State
                              </label>
                            </div>
                            <div className="col-xs-3 input-with-placeholder-group">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder no-padding-md"
                                id="service_address_postal_code"
                                name="service_address_postal_code"
                                value={v('service_address_postal_code')}
                                placeholder="Postal Code"
                                maxLength={12}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label
                                className="input-placeholder"
                                htmlFor="service_address_postal_code"
                              >
                                Postal Code
                              </label>
                            </div>
                            <div className="col-xs-3 input-with-placeholder-group">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder"
                                id="service_address_country"
                                name="service_address_country"
                                value={v('service_address_country')}
                                placeholder="Country"
                                maxLength={15}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label
                                className="input-placeholder"
                                htmlFor="service_address_country"
                              >
                                Country
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12">
                      <div className="row">
                        <div className="label-text col-xs-3">Contact</div>
                        <div className="col-xs-9 input-vertical-distance">
                          <div className="row">
                            <div className="col-xs-6 input-with-placeholder-group ">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder"
                                id="service_co_contact_person"
                                name="service_co_contact_person"
                                value={v('service_co_contact_person')}
                                placeholder="Name"
                                maxLength={25}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label
                                className="input-placeholder"
                                htmlFor="service_co_contact_person"
                              >
                                Name
                              </label>
                            </div>
                            <div className="col-xs-6 input-with-placeholder-group">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder phone-number-input"
                                id="service_contact_tel_mobile"
                                name="service_contact_tel_mobile"
                                value={v('service_contact_tel_mobile')}
                                placeholder="Mobile"
                                maxLength={15}
                                pattern={PHONE_PATTERN}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label
                                className="input-placeholder"
                                htmlFor="service_contact_tel_mobile"
                              >
                                Mobile
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="col-xs-offset-3 col-xs-9 input-vertical-distance">
                          <div className="row">
                            <div className="col-xs-6 input-with-placeholder-group">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder phone-number-input"
                                id="service_contact_tel_office"
                                name="service_contact_tel_office"
                                value={v('service_contact_tel_office')}
                                placeholder="Office tel."
                                maxLength={15}
                                pattern={PHONE_PATTERN}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label
                                className="input-placeholder"
                                htmlFor="service_contact_tel_office"
                              >
                                Office tel.
                              </label>
                            </div>
                            <div className="col-xs-6 input-with-placeholder-group">
                              <ClearableInput
                                className="col-xs-12 input-with-placeholder press_english"
                                type="email"
                                id="service_contact_email"
                                name="service_contact_email"
                                value={v('service_contact_email')}
                                placeholder="Email"
                                maxLength={35}
                                locked={locked}
                                onCommit={commit}
                              />
                              <label className="input-placeholder" htmlFor="service_contact_email">
                                Email
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12">
                      <div className="row">
                        <div className="label-text col-xs-3">Notes</div>
                        <div className="col-xs-9 input-vertical-distance">
                          <div className="row">
                            <div className="col-xs-12 input-with-placeholder-group ">
                              <OrderTextarea
                                className="long-input textarea"
                                clearClassName="text-clear notes-text-clear icon-close-1"
                                name="order_note"
                                value={v('order_note')}
                                maxLength={84}
                                locked={locked}
                                onCommit={commit}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <button
        className={`icon-close-1 delete-order ka-has-tooltip${locked ? ' non-clickable' : ''}`}
        tooltip="Delete"
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
          if (locked) return;
          setShowDelete(true);
        }}
      />
      {showDelete && (
        <DeleteOrderPopup
          title="Delete Service Order?"
          content="If you choose to Delete, all Service Order information will be lost and will not be recoverable."
          name={v('order_number')}
          onConfirm={() => deleteOrder({ projectId, id: order.id })}
          onClose={() => setShowDelete(false)}
        />
      )}
    </li>
  );
};

export default ServiceOrderCard;
